import React, { useState, useEffect, useCallback } from 'react';
import { useScore } from '../../context/ScoreProvider';
import '../../styles/InputQuizScreen.css';

export default function InputQuizScreen({
  question,
  inputs,
  image,
  keypad,
  feedback,
  onCheckStateChange,
  screenId,
  screenScore
}) {
  const { submitAnswer } = useScore();
  const [values, setValues] = useState(() =>
    inputs.map(() => '')
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [isChecked, setIsChecked] = useState(false);

  // handle number press
  const handleKeyPress = (value) => {
    if (isChecked) return;

    setValues((prev) => {
      const next = [...prev];
      next[activeIndex] = value;
      return next;
    });

    if (activeIndex < inputs.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  // allow clicking input field to edit
  const handleFieldClick = (index) => {
    if (isChecked) return;
    setActiveIndex(index);
  };

  const handleCheck = useCallback(() => {
    if (values.some(v => v === '')) return;

    const nextIsCorrect = inputs.every(
      (input, index) => values[index] === input.correctValue
    );

    setIsChecked(true);
    if (screenScore != null) {
      submitAnswer(screenId, screenScore, nextIsCorrect);
    }
  }, [inputs, screenId, screenScore, submitAnswer, values]);

  const isCorrect = inputs.every(
    (input, index) => values[index] === input.correctValue
  );

  useEffect(() => {
    onCheckStateChange?.({
      checked: isChecked,
      canCheck: values.every(v => v !== ''),
      onCheck: handleCheck,
    });
  }, [values, isChecked, handleCheck, onCheckStateChange]);

  const showFeedback =
    isChecked &&
    ((isCorrect && feedback?.correct) ||
     (!isCorrect && feedback?.incorrect));

  const feedbackClass = isCorrect
    ? 'quiz-feedback-correct'
    : 'quiz-feedback-incorrect';

  const feedbackContent = isCorrect
    ? feedback?.correct
    : feedback?.incorrect;

  return (
    <div className="input-quiz quiz-screen">
      <div className={`quiz-card ${showFeedback ? 'blurred' : ''}`}>

        {question && (
          <div
            className={`
              quiz-question
              font-${question?.fontSize ?? 'md'}
              text-${question?.textAlign ?? 'left'}
            `}
            dangerouslySetInnerHTML={{ __html: question.value }}
          />
        )}

        <div className="quiz-input-area">
          {image && (
            <div className="quiz-image">
              <img src={image} alt="" />
            </div>
          )}

          <div className="input-quiz-fields">
            {inputs.map((_, index) => {
              let stateClass = '';
              if (isChecked) {
                stateClass =
                  values[index] === inputs[index].correctValue
                    ? 'correct'
                    : 'incorrect';
              }

              return (
                <div
                  key={index}
                  onClick={() => handleFieldClick(index)}
                  className={`
                    input-quiz-field
                    ${stateClass}
                    ${index === activeIndex ? 'active' : ''}
                  `}
                >
                  {values[index] || ''}
                </div>
              );
            })}
          </div>
        </div>

        <div className="input-quiz-keypad">
          {keypad.values.map((num) => (
            <button
              key={num}
              onClick={() => handleKeyPress(num)}
              className="quiz-option"
            >
              {num}
            </button>
          ))}
        </div>

        {showFeedback && (
          <div
            className={`quiz-feedback ${feedbackClass}`}
            dangerouslySetInnerHTML={{ __html: feedbackContent }}
          />
        )}

      </div>
    </div>
  );
}
